import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const HomeAbout = () => {
  const sectionRef = useRef();
  const stickyRef = useRef();
  const titleRef = useRef();
  const subtitleRef = useRef();
  const bioTextRef = useRef();

  useEffect(() => {
    const section = sectionRef.current;
    const sticky = stickyRef.current;
    const title = titleRef.current;
    const subtitle = subtitleRef.current;
    const bioText = bioTextRef.current;

    // Timeline principale avec ScrollTrigger
    const mainTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top top",
        end: "+=300%",
        scrub: 1,
        pin: sticky,
        anticipatePin: 1,
      }
    });

    mainTimeline
      // Phase 1 : Titre principal apparaît et reste
      .fromTo(title,
        { opacity: 0, y: 60 },
        { opacity: 1, y: 0, duration: 1, ease: "power2.out" },
        0
      )
      .fromTo(subtitle,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1, ease: "power2.out" },
        0.3
      )
      
      // Phase 2 : Titre sort, biographie entre
      .to([title, subtitle],
        { opacity: 0, y: -60, duration: 0.8, ease: "power2.in" },
        1.5
      )
      .fromTo(bioText,
        { opacity: 0, y: 60 },
        { opacity: 1, y: 0, duration: 1.2, ease: "power2.out" },
        2
      );

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div ref={sectionRef} className="relative bg-white" id="about">
      {/* Scroll indicator */}
      <div className="fixed top-8 left-1/2 -translate-x-1/2 z-50 opacity-40">
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs font-light text-gray-900 tracking-[0.3em] uppercase">
            Scroll
          </span>
          <div className="w-[1px] h-16 bg-gray-900/20" />
        </div>
      </div>

      {/* Sticky container */}
      <div 
        ref={stickyRef}
        className="relative h-screen w-full flex items-center justify-center overflow-hidden"
      >
        {/* Phase 1: Titre principal */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-20 px-6">
          <h1 
            ref={titleRef}
            className="text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-light text-gray-900 tracking-tight text-center opacity-0"
          >
            Get to Know
          </h1>
          <h2 
            ref={subtitleRef}
            className="text-7xl md:text-8xl lg:text-9xl xl:text-[12rem] font-bold text-gray-900 tracking-tight text-center mt-2 opacity-0"
            style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            Safaa
          </h2>
        </div>

        {/* Biographie complète */}
        <div
          ref={bioTextRef}
          className="absolute inset-0 flex items-center justify-center z-30 opacity-0 px-6 md:px-12 lg:px-20"
        >
          <div className="max-w-5xl">
            <p className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-light text-gray-900 leading-relaxed md:leading-relaxed lg:leading-relaxed text-center">
              Born in <span className="font-normal">Casablanca</span>, raised in{' '}
              <span className="font-normal">Granada (Spain)</span> and currently based in{' '}
              <span className="font-normal">London</span>.
              <br /><br />
              Influenced by legends such as <span className="font-normal italic">Whitney Houston</span>,{' '}
              <span className="font-normal italic">Mariah Carey</span>,{' '}
              <span className="font-normal italic">Etta James</span> and{' '}
              <span className="font-normal italic">Aretha Franklin</span>, Safaa has developed a{' '}
              <span className="font-normal">unique style</span> that combines the emotional depth of soul with the sophistication of R&B.
              <br /><br />
              At the age of 16, and without anyone's help, she left her home in search of{' '}
              <span className="font-normal">freedom</span> and a dream that was only supported by her talent and determination.
              <br /><br />
              Since then, Safaa has followed a <span className="font-normal">challenging but rewarding path</span>.
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 640px) {
          h1 {
            font-size: 2.5rem;
            line-height: 1.1;
          }
          h2 {
            font-size: 3rem;
            line-height: 1.1;
          }
        }
      `}</style>
    </div>
  );
};

export default HomeAbout;